import { useSetToast } from "@app/components/Toast";
import { useS3FilesManagement } from "@app/hooks/createFileHook";
import {
    newS3FileToCreateFileInput,
    type NewS3File,
} from "@app/usecases/FileUseCases";
import { Tooltip } from "@design-system/DataDisplay/Tooltip";
import { Icon } from "@design-system/Icon";
import { Button } from "@design-system/Inputs/Button";
import { Chip } from "@design-system/Inputs/Chip";
import DropZone from "@design-system/Inputs/DropZone";
import { Dropdown } from "@design-system/Inputs/UniversalDropdown";
import { Box } from "@design-system/Layout/Box";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { Modal } from "@design-system/Modal";
import { Text } from "@design-system/Typography/Text";
import { useMemo, useState, type FC } from "react";
import {
    useFrameworkQuestionTags,
    useGenerateCdpWorkplan,
} from "../../../data";
import { useGenerateCdpWorkplanModalTranslations } from "./i18n";

const THEMATICS = [
    {
        id: "1",
        name: "Climate Change",
    },
    {
        id: "2",
        name: "Water",
    },
    {
        id: "3",
        name: "Forest",
    },
] as const;

export const GenerateCdpWorkplanModal: FC<{
    frameworkId: string;
}> = ({ frameworkId }) => {
    const { t, keys } = useGenerateCdpWorkplanModalTranslations();
    const [open, setOpen] = useState(false);
    const [selectedThematics, setSelectedThematics] = useState<
        string | undefined
    >(undefined);
    const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
    const [uploadedFile, setUploadedFile] = useState<NewS3File | undefined>(
        undefined,
    );

    const { generateCdpWorkplan, isPending } = useGenerateCdpWorkplan();

    const { setToastSuccess, setToastError } = useSetToast();

    const { tags } = useFrameworkQuestionTags(frameworkId, 2025, "en");
    const { uploadFiletoS3 } = useS3FilesManagement();

    const handleFileDrop = async (file: File) => {
        const newS3File = await uploadFiletoS3(file);
        setUploadedFile(newS3File);
    };

    const handleDeleteFile = () => {
        setUploadedFile(undefined);
    };

    const options = THEMATICS.map((thematic) => ({
        value: thematic.id,
        label: thematic.name,
    }));

    const handleThematicChange = (value: string[]) => {
        if (value.length > 0) {
            setSelectedThematics(value[value.length - 1]);
        } else {
            setSelectedThematics(undefined);
        }
    };

    const handleTagClickMaker = (tagId: string) => () => {
        setSelectedTagIds((prev) => {
            if (prev.includes(tagId)) {
                return prev.filter((id) => id !== tagId);
            }
            return [...prev, tagId];
        });
    };

    const handleGenerateWorkplan = async () => {
        if (uploadedFile) {
            generateCdpWorkplan(
                {
                    thematic:
                        THEMATICS.find((t) => t.id === selectedThematics)
                            ?.name ?? "",
                    categories: selectedTagIds,
                    file: newS3FileToCreateFileInput(uploadedFile),
                },
                {
                    onSuccess: () => {
                        setToastSuccess(t(keys.generate_workplan_success));
                        setOpen(false);
                        setUploadedFile(undefined);
                        setSelectedThematics(undefined);
                        setSelectedTagIds([]);
                    },
                    onError: () => {
                        setToastError(t(keys.generate_workplan_error));
                    },
                },
            );
        }
    };

    const handleCancel = (open: boolean) => () => {
        setOpen(open);
        setSelectedThematics(undefined);
        setSelectedTagIds([]);
        setUploadedFile(undefined);
    };

    const isSelected = (tagId: string) => {
        return selectedTagIds.includes(tagId);
    };

    const acceptedFileTypes = [
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    if (frameworkId !== "cdp-main") {
        return null;
    }

    const isDisabled = useMemo(
        () => !uploadedFile || !selectedThematics || !selectedTagIds.length,
        [uploadedFile, selectedThematics, selectedTagIds],
    );

    return (
        <Modal open={open} onOpenChange={handleCancel(!open)}>
            <Modal.Trigger asChild>
                <FlexRow alignItems="center" gap="2" className="cursor-pointer">
                    <Icon name="stars" color="#BE2FBE" size="sm" />
                    {t(keys.open_button)}
                </FlexRow>
            </Modal.Trigger>
            <Modal.Overlay centerContent className="py-5" scrollable>
                <Modal.Content className="w-fit max-w-[880px]">
                    <Modal.Header>
                        <Modal.Title>{t(keys.generate_workplan)}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <FlexCol gap="4">
                            <Text variant="body" color="secondary">
                                {t(keys.generate_workplan_description)}
                            </Text>
                            {uploadedFile ? (
                                <FlexCol gap="2">
                                    <Text variant="body-bold">
                                        {t(keys.upload_cdp_scorecard)}
                                    </Text>
                                    <FlexRow
                                        justifyContent="start"
                                        alignItems="center"
                                        gap="2"
                                    >
                                        <Text variant="body">
                                            {uploadedFile.name}
                                        </Text>
                                        <Button
                                            variant="plain"
                                            size="sm"
                                            onClick={handleDeleteFile}
                                        >
                                            <Button.Icon name="trash" />
                                        </Button>
                                    </FlexRow>
                                </FlexCol>
                            ) : (
                                <DropZone
                                    accept={acceptedFileTypes}
                                    buttonText="Upload"
                                    multiple={false}
                                    size="lg"
                                    className="w-full h-full"
                                    onChange={handleFileDrop}
                                    loading={false}
                                >
                                    {t(keys.upload_cdp_scorecard)}
                                </DropZone>
                            )}
                            <FlexCol gap="2">
                                <Text variant="body-bold">
                                    {t(keys.select_thematics_title)}
                                </Text>
                                <Text variant="body" color="secondary">
                                    {t(keys.select_thematics_description)}
                                </Text>
                                <FlexCol className="w-fit">
                                    <Dropdown
                                        value={
                                            selectedThematics
                                                ? [selectedThematics]
                                                : []
                                        }
                                        onValueChange={handleThematicChange}
                                        selectType="multi"
                                    >
                                        <Dropdown.ChipFilterTrigger
                                            label={t(keys.select_thematics)}
                                            options={options}
                                            valueIdentifierKey="value"
                                            valueDisplayKey="label"
                                        />

                                        <Dropdown.Content align="start">
                                            {options.map((thematic) => (
                                                <Dropdown.SelectableItem
                                                    value={thematic.value}
                                                    key={thematic.value}
                                                >
                                                    <Dropdown.ItemText>
                                                        {thematic.label}
                                                    </Dropdown.ItemText>
                                                    <Dropdown.SelectedIndicator />
                                                </Dropdown.SelectableItem>
                                            ))}
                                        </Dropdown.Content>
                                    </Dropdown>
                                </FlexCol>
                            </FlexCol>
                            <FlexCol gap="2">
                                <Text variant="body-bold">
                                    {t(keys.select_tags)}
                                </Text>
                                <Text variant="body" color="secondary">
                                    {t(keys.select_tags_description)}
                                </Text>
                                {tags?.length && (
                                    <FlexRow wrap="wrap" gap="2" w="full">
                                        {tags.map((tag) => (
                                            <Chip
                                                key={tag.id}
                                                active={isSelected(tag.id)}
                                                onClick={handleTagClickMaker(
                                                    tag.id,
                                                )}
                                            >
                                                <Chip.Value>
                                                    {tag.name}
                                                </Chip.Value>
                                            </Chip>
                                        ))}
                                    </FlexRow>
                                )}
                            </FlexCol>
                            <FlexRow justifyContent="end" gap="2" w="full">
                                <Button
                                    variant="outlined"
                                    size="sm"
                                    onClick={handleCancel(false)}
                                >
                                    {t(keys.cancel)}
                                </Button>
                                <Tooltip>
                                    <Tooltip.Trigger asChild>
                                        <Box>
                                            <Button
                                                variant="flatPrimary"
                                                size="sm"
                                                onClick={handleGenerateWorkplan}
                                                disabled={isDisabled}
                                                loading={isPending}
                                            >
                                                {t(keys.generate_workplan)}
                                            </Button>
                                        </Box>
                                    </Tooltip.Trigger>
                                    {isDisabled && (
                                        <Tooltip.Content>
                                            {t(keys.generate_workplan_tooltip)}
                                        </Tooltip.Content>
                                    )}
                                </Tooltip>
                            </FlexRow>
                        </FlexCol>
                    </Modal.Body>
                </Modal.Content>
            </Modal.Overlay>
        </Modal>
    );
};
